import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Eye, Trash2 } from 'lucide-react';

interface Session {
  id: number;
  user: {
    id: number;
    email_address: string;
  };
  ip_address: string;
  user_agent: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  sessions: Session[];
  pagination: Pagination;
}

const SessionsIndex: React.FC<Props> = ({ sessions, pagination }) => {
  const truncateUserAgent = (userAgent: string) => {
    return userAgent.length > 50 ? userAgent.substring(0, 50) + '...' : userAgent;
  };

  return (
    <AdminLayout title="Gestion des sessions">
      <Head title="Sessions - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Sessions</h1>
            <p className="text-gray-600">
              {pagination.total_count} session{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
        </div>

        {/* Sessions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des sessions actives</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Utilisateur</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">IP</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Navigateur</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Créée le</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">#{session.id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        <Link 
                          href={`/admin/users/${session.user.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {session.user.email_address}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {session.ip_address || 'Non spécifié'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        <span title={session.user_agent}>
                          {truncateUserAgent(session.user_agent || 'Non spécifié')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(session.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Link href={`/admin/sessions/${session.id}`}>
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link 
                            href={`/admin/sessions/${session.id}`} 
                            method="delete" 
                            as="button"
                            preserveScroll
                            onBefore={() => {
                              if (!confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) {
                                return false;
                              }
                            }}
                          >
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/admin/sessions?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/admin/sessions?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SessionsIndex; 
